<template>
  <div
    ref="listingPage"
    class="listing-page"
  >
    <ToursFiltersSection
      :currencyCode
      :slug
      :page
      :location
      :geoObject
      :from-city-seo-payload="fromCitySeoPayload"
      :defaultSoguFields
      @filters-updated="onFiltersUpdated"
      @tagChanged="currentTag = $event"
    />

    <TourExpSelectionBlock
      v-if="isTopSelectionVisible"
      :slug="slug"
      :location="location"
      :context-geo-subject="defaultSoguFields"
      :page="page"
      :is-extra-selection="false"
      :position="'top'"
    />

    <TourAllCardsBlock
      :location="location"
      :slug="slug"
      :geo-object="geoObject"
      :from-city-seo-payload="fromCitySeoPayload"
      :page="page"
      :default-sogu-fields="defaultSoguFields"
      @pageSelected="handleNewPage"
      @excludedIdsUpdated="excludedIds = $event"
    />

    <TourExpSelectionBlock
      v-if="isLocation.isRegion"
      :slug="slug"
      :location="location"
      :context-geo-subject="defaultSoguFields"
      :page="page"
      :is-extra-selection="true"
      :position="'bottom'"
    />
  </div>

  <section
    v-if="shouldShowRecommendationSection"
    class="wrap"
  >
    <h2
      v-if="showSmallOutput"
      class="recommendations-title"
    >
      {{ t('page.recommendationTitle') }}
    </h2>
    <TourCardsList
      class="recommendations-list"
      sogu-block="section"
      :recommendation-listing-sogu-params="recommendationListingSoguParams"
      :exp-list="experienceRecommendation"
      :context-geo-subject="defaultSoguFields"
      :show-type="false"
      :location="location"
    />
  </section>

  <ToursAttractionsBlock
    :geo-object="geoObject"
    :slug="slug"
    :location="location"
    :sightsUrl="sightsUrl"
    :default-sogu-fields="defaultSoguFields"
  />

  <TourGuidesBlock
    :slug="slug"
    :location="location"
    :default-sogu-fields="defaultSoguFields"
    :geo-object="geoObject"
  />

  <div class="wrap">
    <ReviewsTourBlock
      :show-skeleton="isSkeletonActive"
      sogu-prefix="listing"
      :params="reviewsParams"
      :sogu-params="defaultSoguFields.soguStamp"
    />
  </div>

  <ArticlesBlock
    :slug="slug"
    :location="location"
    :geo-object="geoObject"
    :default-sogu-fields="defaultSoguFields"
  />

  <SubscriptionBlock
    v-if="!loaderStore.isFiltersLoading"
    class="wrap"
    :api-url="VITE_RUNTIME_API_URL"
    :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL"
  />

  <TourSeoExperienceWrapper
    v-if="!loaderStore.isFiltersLoading"
    :slug="slug"
    :current-tag="currentTag"
    :from-city-seo-payload="fromCitySeoPayload"
    :default-sogu-fields="defaultSoguFields"
    :location="location"
  />
</template>

<script lang="ts" setup>
  import { useRoute } from 'vue-router';
  import { TourSeoExperienceWrapper } from '@/widgets/seoBlock/tour';
  import { TourGuidesBlock } from '@/widgets/guidesBlock/tour';
  import { computed, inject, ref, type useShadowRoot, watch } from 'vue';
  import { useCityInfo } from '@/entities/city/api/useCityInfo.ts';
  import { useCountryInfo } from '@/entities/country/api/useCountryInfo.ts';
  import { useRegionInfo } from '@/entities/region/api/useRegionInfo.ts';
  import type { CityInfo, CountryInfo, RegionInfo } from '@/shared/api/experience';
  import { ReviewsTourBlock, type IReviewsBlockRequestParams } from '@experience/travelers-reviews-package';
  import { TourAllCardsBlock } from '@/widgets/allCardsBlock/tour';
  import { TourExpSelectionBlock } from '@/widgets/expSelectionsBlock/tour';
  import { type ContextGeoSubject, GEO_TYPES } from '@/shared/types/geography';
  import { makeFullLink } from '@/shared/lib/makeFullLink';
  import { filterScroll, paginationScroll } from '@/shared/lib/use-scroll';
  import ToursFiltersSection from './ToursFiltersSection.vue';
  import { tourFiltersToQuery, useTourFiltersStore } from '@/entities/filter/store/tour';
  import { reportMFRendered } from '@experience/core-hostapp-share-lib';
  import { useRuntimeConfig } from 'virtual:runtime-config';
  import { useUserStore } from '@/entities/user';
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import { useExperienceFavorites } from '@/entities/experience/favorite';
  import { useTourPageViewTracker, useTourSoguStore } from '@/features/sogu/tour';
  import { type CurrencyCode, toCurrencyCode } from '@/shared/lib/currency';
  import { useScrollRestoration } from '@/shared/composables/useScrollRestoration';
  import { useListingRouteParams } from '../../composables/useListingRouteParams';
  import { useLoaderStore } from '@/shared/store/useLoaderStore';
  import { TourCardsList } from '@/widgets/allCardsBlock/tour';
  import { useTourSmallOutput } from '@/features/smallOutput/tour';
  import { useGetExperiencesRecommendations } from '@/entities/experience/recommendation';

  import { useI18n } from '@experience/i18n';
  import ru from '../../../i18n/ru.json';
  import { useExperienceWithMovementType } from '@/entities/movement-type';
  import { useRefHistory } from '@vueuse/core';
  import type { ExtendedPartTagType } from '@/entities/rubrics';
  import { EXP_TYPES } from '@/entities/filter';
  import { LocationType } from '@/entities/location';
  import { ArticlesBlock } from '@/widgets/articlesBlock/ui';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';
  import { ToursAttractionsBlock } from '@/widgets/attractionsToursBlock/ui/tour';
  import { sendMindboxEvent } from '@experience/core-consent-package';
  import { useScreenSize } from '@experience/shared';
  import { buildTourMindboxSearchPayload } from '@/pages/listing/lib/tour/TourMindboxSearchPayload';
  import { useMindboxSearchEvent, useMindboxSearchLabelMaps } from '@/pages/listing/composables';

  type MessageSchema = typeof ru;

  const { t } = useI18n<
    {
      message: MessageSchema;
    },
    'ru'
  >({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const { VITE_RUNTIME_API_URL, VITE_RUNTIME_SOGU_SERVICE_URL } = useRuntimeConfig<false>();

  const route = useRoute();
  const router = useRouter();

  // TODO: отрефакторить код задача - EXP-26201
  const MF_NAME = 'experience-mf-listing';

  useScrollRestoration();

  const mindBoxDefaultData = computed(() => {
    const ids = {} as {
      cityProducts?: number;
      regionProducts?: number;
    };

    if (isLocation.value.isCity) {
      ids.cityProducts = geoObject.value.id ?? 0;
    }

    if (isLocation.value.isRegion) {
      ids.regionProducts = geoObject.value.id ?? 0;
    }

    return {
      data: {
        viewProduct: {
          product: {
            ids,
          },
        },
      },
      operation: 'ViewZone',
    };
  });

  const { loadFavorites } = useExperienceFavorites();
  const { showSmallOutput } = useTourSmallOutput();
  const excludedIds = ref([]);

  const excludedIdsParams = computed(() => excludedIds.value.join(',') || '');

  const userStore = useUserStore();
  userStore.getUser();

  // прокидываем router в store
  const filterStore = useTourFiltersStore();
  filterStore.initRouter(router);

  const loaderStore = useLoaderStore();
  const { isSkeletonActive } = useListingSkeleton();

  const { slug, location, isLocation, fromCitySeoPayload } = useListingRouteParams();

  const isTopSelectionVisible = computed(() => isLocation.value.isRegion);

  const currentTag = ref<ExtendedPartTagType | null>(null);

  const { countryInfo } = useCountryInfo(
    slug,
    computed(() => isLocation.value.isCountry),
  );
  const { regionInfo } = useRegionInfo(
    slug,
    computed(() => isLocation.value.isRegion),
    computed(() => route.params.country),
  );
  const { cityInfo } = useCityInfo(
    slug,
    computed(() => isLocation.value.isCity),
  );

  const geoObject = computed(
    () => cityInfo.value || regionInfo.value || countryInfo.value || ({} as CityInfo | CountryInfo | RegionInfo),
  );

  const soguStore = useTourSoguStore();
  const { formatLabels, durationLabels, sortingLabels, comfortLabels, accommodationLabels, activityLevelLabels } =
    useMindboxSearchLabelMaps();

  useMindboxSearchEvent(() => {
    const geoName =
      (cityInfo.value as CityInfo | undefined)?.name_ru ??
      (countryInfo.value as CountryInfo | undefined)?.name_ru ??
      (regionInfo.value as RegionInfo | undefined)?.name ??
      '';

    if (!geoName) {
      return null;
    }

    return buildTourMindboxSearchPayload({
      searchState: filterStore.searchState,
      pageParams: filterStore.pageParams,
      cityName: geoName,
      countryName: geoName,
      formatLabels: formatLabels.value ?? undefined,
      durationLabels: durationLabels.value ?? undefined,
      sortingLabels: sortingLabels.value ?? undefined,
      comfortLabels: comfortLabels.value ?? undefined,
      accommodationLabels: accommodationLabels.value ?? undefined,
      activityLevelLabels: activityLevelLabels.value ?? undefined,
    });
  });

  watch(
    () => geoObject.value,
    (value) => {
      soguStore.setGeoObject(value);
      if (value.id) {
        sendMindboxEvent({
          ...mindBoxDefaultData.value,
        });
      }
    },
    { deep: true, immediate: true },
  );

  const requestParams = computed(() => {
    const isBookable = filterStore.isFiltersApplied ? 'true' : 'all';

    return {
      ...tourFiltersToQuery(filterStore.searchState, filterStore.pageParams),
      bookable: isBookable,
      price_format: 'travelers' as const,
      exclude_ids: excludedIdsParams.value,
    };
  });

  const { experiencesData, recommendationAlgorithmMeta } = useGetExperiencesRecommendations(
    requestParams,
    showSmallOutput,
  );

  const recommendationListingSoguParams = computed(() => ({
    section_name: t('page.recommendationTitle'),
    block_type: 'recommendations' as const,
    ...recommendationAlgorithmMeta.value,
  }));

  const experienceRecommendation = useExperienceWithMovementType({
    experiences: computed(() => {
      if (Array.isArray(experiencesData)) {
        return experiencesData;
      }

      return experiencesData?.value?.results ?? [];
    }),
  });

  const currencyCode = computed<CurrencyCode>(() => {
    const geo = geoObject.value;
    let currencyValue: string | undefined;

    if ('currency' in geo && geo.currency) {
      currencyValue = geo.currency;
    } else if ('country' in geo && geo.country && 'currency' in geo.country && geo.country.currency) {
      currencyValue = geo.country.currency;
    } else if (
      'countries' in geo &&
      Array.isArray(geo.countries) &&
      geo.countries.length > 0 &&
      geo.countries[0].currency
    ) {
      currencyValue = geo.countries[0].currency;
    }

    return toCurrencyCode(currencyValue);
  });

  // Текущая страница
  const page = computed(() => {
    return Number(route.query?.page ?? 1);
  });

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);

  const handleNewPage = (newPage: number, isScroll = false) => {
    const params = structuredClone(route.query);

    if (newPage === 1) {
      // Чтобы не записывалось в url page на первой странице
      delete params.page;
    } else {
      params.page = newPage.toString();
    }

    router.push({
      query: {
        ...params,
      },
    });
    if (isScroll) {
      // Не успевает пеерсчитать высоту блока, нужен timeout
      paginationScroll(rootNode);
      handleListingPageViewEvent();
    }
  };

  const sightsUrl = computed(() => {
    if (isLocation.value.isCity) {
      return makeFullLink(`/attractions/${slug.value}/`);
    }

    if (isLocation.value.isRegion) {
      const { regionSlug, country } = route.params;

      return `/tours/${country}/${regionSlug}`;
    }

    return `/tours/${slug.value}/`;
  });

  const defaultSoguFields = computed<ContextGeoSubject>(() => {
    let subjectGeoType: ContextGeoSubject['soguStamp']['subject_geo_type'] = GEO_TYPES.COUNTRY;

    if (isLocation.value.isCity) {
      subjectGeoType = GEO_TYPES.CITY;
    } else if (isLocation.value.isRegion) {
      subjectGeoType = GEO_TYPES.REGION;
    } else if (isLocation.value.isCountry) {
      subjectGeoType = GEO_TYPES.COUNTRY;
    }

    return {
      soguStamp: {
        subject_geo_id: geoObject.value.id ?? 0,
        subject_geo_name:
          (geoObject.value as CityInfo)?.name_ru ||
          (geoObject.value as CountryInfo)?.name_ru ||
          (geoObject.value as RegionInfo)?.name ||
          '',
        subject_geo_type: subjectGeoType,
      },
    };
  });

  const { trackListingPageViewEvent } = useTourPageViewTracker();

  const listingPage = ref<HTMLElement | null>(null);

  const handleListingPageViewEvent = () => {
    let geo: CityInfo | RegionInfo | CountryInfo | null = null;

    if (isLocation.value.isCity && cityInfo.value) {
      geo = cityInfo.value;
    } else if (isLocation.value.isRegion && regionInfo.value) {
      geo = regionInfo.value;
    } else if (isLocation.value.isCountry && countryInfo.value) {
      geo = countryInfo.value;
    }

    if (geo) {
      trackListingPageViewEvent(soguStore.rubricId, geo, defaultSoguFields.value);
    }
  };

  const unwatchLoader = watch(
    () => loaderStore.isFiltersLoading,
    (value) => {
      if (!value) {
        handleListingPageViewEvent();

        queueMicrotask(() => {
          unwatchLoader();
        });
      }
    },
    { immediate: true },
  );

  // Задаем параметры с query для store
  watch(
    () => [route.query, route.params],
    ([query, params], prevValues) => {
      filterStore.setInitialStore(query, (params?.tag as string) || '');

      // Сброс флагов загрузки при изменении параметров роута
      if (prevValues) {
        const [, prevParams] = prevValues;
        if (prevParams && JSON.stringify(params) !== JSON.stringify(prevParams)) {
          const prevParamsObj = prevParams as Record<string, unknown>;
          const currentParamsObj = params as Record<string, unknown>;

          // проверяем изменение геопараметров локации
          const locationKeys = ['regionSlug', 'country', 'countrySlug', 'citySlug'];
          const locationChanged = locationKeys.some((key) => prevParamsObj[key] !== currentParamsObj[key]);

          // Reset flags based on what changed
          if (locationChanged) {
            loaderStore.ready.seo = false;
            loaderStore.ready.rubrics = false;
          }
          // Tag change is handled by CoverBlock watcher
        }
      }
    },
    { deep: true, immediate: true },
  );

  const observer = ref<IntersectionObserver | null>(null);

  onBeforeUnmount(() => {
    observer.value?.disconnect();
  });

  onMounted(() => {
    reportMFRendered(MF_NAME);
    if (geoObject.value.id) {
      sendMindboxEvent({ ...mindBoxDefaultData.value });
    }
  });

  loadFavorites();

  const { history } = useRefHistory(
    computed(() => filterStore.state),
    { deep: true },
  );

  const { isMobile } = useScreenSize();

  function onFiltersUpdated() {
    if (loaderStore.isFiltersLoading) {
      return;
    }

    const last = history.value[2];
    const currentFilters = toRaw(filterStore.state);

    const changedKeys: string[] = [];

    Object.keys(currentFilters).forEach((key) => {
      const lastValue = JSON.stringify(last?.snapshot[key as keyof (typeof last)['snapshot']]);
      const currentValue = JSON.stringify(currentFilters[key as keyof typeof currentFilters]);

      if (lastValue !== currentValue) {
        changedKeys.push(key);
      }
    });

    const routeQueryKeys = Object.keys(route.query);

    // Проверка на случай, если фильтры были сброшены до по умолчанию при изменении сортировки на дефолтную
    const isOnlySortingReset = changedKeys.length === 1 && changedKeys[0] === 'sorting';

    const isReset = routeQueryKeys.length === 0 && !isOnlySortingReset;

    const isPageQuery = routeQueryKeys.includes('page');

    if (!isPageQuery && !isMobile.value) {
      setTimeout(() => {
        filterScroll(rootNode, isReset, false);
      });
    }
    handleListingPageViewEvent();
  }

  const shouldShowRecommendationSection = computed(() => {
    return experienceRecommendation.value.length > 0 && showSmallOutput.value;
  });

  const reviewsParams = computed<IReviewsBlockRequestParams>(() => {
    const params: IReviewsBlockRequestParams = {
      exp_type: EXP_TYPES.TOUR,
    };

    switch (location.value) {
      case LocationType.CITY:
        params.city__slug = slug.value;
        break;
      case LocationType.REGION:
        params.region__slug = slug.value;
        break;
      case LocationType.COUNTRY:
        params.country__slug = slug.value;
        break;
      default:
        throw Error('Неизвестный LocationType');
    }

    return params;
  });

  const verticalsMenuSelect = () => {
    /**
     * отправляем событие для подсвечивания пункта меню в "вертикалях"
     */
    document.dispatchEvent(
      new CustomEvent('updateVerticalsMenuActive', {
        detail: {
          active: 'tours',
        },
      }),
    );
  };

  watch(
    () => route.path,
    () => {
      if (geoObject.value.id) {
        sendMindboxEvent({ ...mindBoxDefaultData.value });
      }

      verticalsMenuSelect();
    },
  );

  onMounted(() => {
    verticalsMenuSelect();
  });
</script>

<style lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;
  @use '@experience/design-tokens/typography' as typography;

  // TODO
  // Временное решение для скрытия второго скролла в модалке
  // Если все хорошо - нужно перенести в uikit
  /* stylelint-disable-next-line order/order */
  .ui-modal__scroll-container::-webkit-scrollbar {
    display: none !important;
  }

  // И это тоже. В фильтрах чекбоксы времени начала перекрывают z-index 2
  .ui-modal-header_fixed {
    z-index: 10 !important;
  }

  .listing-page {
    padding-top: 40px;
    scrollbar-gutter: stable;

    @include adaptivity.mobile {
      padding-top: 24px;
    }
  }

  .wrap {
    box-sizing: border-box;
    min-width: 950px;
    max-width: 1260px;
    margin: 0 auto;
    padding: 0 20px;

    @media only screen and (max-width: 1024px) {
      width: 100%;
      min-width: 100%;
      max-width: 100vw;
      padding: 0 20px;
    }
  }

  .azure-link {
    color: var(--brand-color);
    text-decoration: none;
    cursor: pointer;
  }

  .azure-dashed {
    border-bottom: 1px dashed var(--brand-color);
  }

  .blue-link {
    color: #2e78c1;
    text-decoration: none;
    cursor: pointer;
  }

  .recommendations-title,
  .popular-title {
    margin-top: 56px;

    @include typography.base-700-500-extra;

    @include adaptivity.mf-desktop {
      margin-top: 80px;

      @include typography.base-800-750-extra;
    }
  }

  .recommendations-list,
  .popular-list {
    margin-top: 16px;

    @include adaptivity.mf-desktop {
      margin-top: 24px;
    }
  }

  .listing-page .ui-chip,
  .listing-page .ui-chip__label-container {
    max-width: inherit;
  }

  .sights-wrap {
    margin-top: 80px;

    @include adaptivity.tablet {
      margin-top: 56px;
    }
  }

  h1,
  h2,
  h3,
  figure {
    margin: 0;
  }

  :host {
    margin: 0;
    scrollbar-gutter: stable;
  }
</style>
